import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useProject } from '../context/ProjectContext';
import { useToast } from '../hooks/useToast';
import { db, authApi } from '../services';
import { User, Role, TaskStatus } from '../types';
import { ERROR_MESSAGES } from '../utils/constants';

const AdminUsers: React.FC = () => {
  const { currentUser } = useAuth();
  const { users, tasks, projects, refreshAll } = useProject();
  const { showSuccess, showError } = useToast();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [viewingStatsUser, setViewingStatsUser] = useState<User | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', role: Role.TEAM_MEMBER });
  const [invitationSuccess, setInvitationSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Initial data is already loaded by ProjectContext, no need to refresh here
  // Only show syncing state when manually triggered

  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleManualRefresh = async () => {
    
    setIsSyncing(true);
    try {
      await refreshAll();
    } catch (err) {
      console.error("Manual refresh failed", err);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSaveUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setIsSyncing(true);
    setErrorMessage(null);
    setInvitationSuccess(false);
    
    try {
      if (editingUser) {
        await db.updateUser(editingUser.id, { role: editingUser.role });
        setEditingUser(null);
        await refreshAll();
      } else {
        const createdUser = await authApi.inviteUser(
          newUser.email, 
          newUser.name, 
          newUser.role
        );

        if (!createdUser) throw new Error("Cloud handshake failed.");

        setInvitationSuccess(true);
        setNewUser({ name: '', email: '', role: Role.TEAM_MEMBER });
        await refreshAll();
      }
    } catch (err: any) {
      console.error('Invitation Error:', err);
      let msg = err.message || "Unknown error occurred.";
      if (msg.includes("422")) {
        msg = "Invitee already exists or signups are disabled in Supabase.";
      }
      setErrorMessage(msg);
    } finally {
      setIsSyncing(false);
    }
  };

  const deleteUser = async (id: string) => {
    
    if (id === currentUser?.id) {
      alert("System Safety: You cannot delete your own administrative account.");
      return;
    }
    
    if (window.confirm('Remove this member? This deletes their database profile.')) {
      setIsSyncing(true);
      try {
        await db.deleteUser(id);
        await refreshAll();
      } catch (err: any) {
        setErrorMessage(`Cloud Error: ${err.message}`);
      } finally {
        setIsSyncing(false);
      }
    }
  };

  const getUserStats = (userId: string) => {
    const userProjects = projects.filter(p => p.memberIds.includes(userId)) || [];
    const userTasks = tasks.filter(t => t.assigneeId === userId) || [];
    const completedTasks = userTasks.filter(t => t.status === TaskStatus.DONE).length;
    
    return {
      projectsCount: userProjects.length,
      tasksCount: userTasks.length,
      completedCount: completedTasks,
      todoCount: userTasks.filter(t => t.status === TaskStatus.TODO).length,
      inProgressCount: userTasks.filter(t => t.status === TaskStatus.IN_PROGRESS).length,
      completionRate: userTasks.length > 0 ? Math.round((completedTasks / userTasks.length) * 100) : 0
    };
  };

  return (
    <div className="animate-in fade-in duration-500 max-w-6xl mx-auto space-y-8 overflow-y-auto h-full p-4 md:p-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Organization Directory</h1>
          <p className="text-slate-600 mt-1 font-medium">Manage members and workspace permissions.</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={handleManualRefresh}
            className={`p-3 text-slate-500 hover:text-indigo-600 hover:bg-white border border-slate-200 rounded-xl transition-all shadow-sm ${isSyncing ? 'animate-spin' : ''}`}
            title="Sync with Cloud"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/></svg>
          </button>
          <button 
            onClick={() => { setShowModal(true); setInvitationSuccess(false); setErrorMessage(null); }}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-500/20 active:scale-95"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"/></svg>
            Invite Member
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl md:rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden min-h-[400px]">
        <div className="p-4 md:p-6 border-b border-slate-100 bg-slate-50/30 flex items-center justify-between">
          <div className="relative max-w-md w-full">
            <input
              type="text"
              placeholder="Search members..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 md:pl-10 pr-4 py-2 md:py-2.5 bg-white border border-slate-200 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 rounded-xl text-xs md:text-sm outline-none transition-all placeholder-slate-400 font-semibold text-slate-900 shadow-sm"
            />
            <div className="absolute left-3 md:left-3.5 top-2 md:top-3 text-slate-400">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto pb-12">
          {filteredUsers.length === 0 ? (
            <div className="py-24 text-center">
              <h3 className="text-xl font-bold text-slate-900 mb-2">No members found</h3>
              <p className="text-slate-500 font-medium max-w-xs mx-auto mb-6">Start by inviting your first team member.</p>
            </div>
          ) : (
            <table className="w-full text-left border-collapse min-w-[600px]">
              <thead>
                <tr className="bg-slate-50/50">
                  <th className="px-4 md:px-8 py-3 md:py-4 text-[10px] md:text-[11px] font-black uppercase tracking-widest text-slate-500 border-b border-slate-100">Member</th>
                  <th className="px-4 md:px-8 py-3 md:py-4 text-[10px] md:text-[11px] font-black uppercase tracking-widest text-slate-500 border-b border-slate-100">Permission</th>
                  <th className="px-4 md:px-8 py-3 md:py-4 text-[10px] md:text-[11px] font-black uppercase tracking-widest text-slate-500 border-b border-slate-100 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {filteredUsers.map(user => (
                  <tr key={user.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 md:px-8 py-4 md:py-5">
                      <div className="flex items-center gap-3 md:gap-4">
                        <img src={user.avatar} className="w-9 h-9 md:w-10 md:h-10 rounded-xl border border-slate-100 shadow-sm" alt="" />
                        <div className="min-w-0">
                          <button
                            onClick={() => setViewingStatsUser(user)}
                            className="font-bold text-sm md:text-base text-slate-900 hover:text-indigo-600 transition-colors block text-left truncate max-w-[150px] md:max-w-none"
                          >
                            {user.name}
                          </button>
                          <p className="text-[11px] md:text-xs text-slate-500 font-medium truncate">{user.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 md:px-8 py-4 md:py-5">
                      <span className={`px-2 md:px-2.5 py-1 rounded-md text-[9px] md:text-[10px] font-black uppercase tracking-wider ${
                        user.role === Role.ADMIN ? 'bg-indigo-600 text-white shadow-md shadow-indigo-200' :
                        user.role === Role.MANAGER ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {user.role.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 md:px-8 py-4 md:py-5 text-right">
                      <div className="flex items-center justify-end gap-2 md:gap-3">
                        <button
                          onClick={() => setEditingUser(user)}
                          className="p-1.5 md:p-2 text-slate-400 hover:text-indigo-600 hover:bg-white border border-transparent rounded-lg transition-all"
                        >
                          <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                        </button>
                        <button
                          onClick={() => deleteUser(user.id)}
                          disabled={user.id === currentUser?.id}
                          className="p-1.5 md:p-2 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all disabled:opacity-20"
                        >
                          <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1v3M4 7h16"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Member Statistics Modal */}
      {viewingStatsUser && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white w-full max-w-2xl rounded-[2.5rem] shadow-2xl p-0 animate-in zoom-in-95 overflow-hidden">
            <div className="bg-slate-900 p-8 flex items-center gap-6 relative">
              <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/20 rounded-full blur-3xl -mr-16 -mt-16"></div>
              <img src={viewingStatsUser.avatar} className="w-24 h-24 rounded-[2rem] border-4 border-white/10 shadow-xl relative z-10" alt="" />
              <div className="relative z-10">
                <h2 className="text-3xl font-black text-white tracking-tight">{viewingStatsUser.name}</h2>
                <div className="flex items-center gap-2 mt-2">
                  <span className="px-3 py-1 bg-indigo-500 text-white text-[10px] font-black uppercase tracking-widest rounded-lg">{viewingStatsUser.role.replace('_', ' ')}</span>
                  <span className="text-slate-400 text-sm font-medium">{viewingStatsUser.email}</span>
                </div>
              </div>
              <button 
                onClick={() => setViewingStatsUser(null)}
                className="absolute top-6 right-6 p-2 text-slate-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>

            <div className="p-10 space-y-10">
              {(() => {
                const s = getUserStats(viewingStatsUser.id);
                return (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-5 bg-slate-50 rounded-3xl border border-slate-100 text-center">
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Projects</p>
                        <p className="text-3xl font-black text-slate-900">{s.projectsCount}</p>
                      </div>
                      <div className="p-5 bg-slate-50 rounded-3xl border border-slate-100 text-center">
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">Total Tasks</p>
                        <p className="text-3xl font-black text-slate-900">{s.tasksCount}</p>
                      </div>
                      <div className="p-5 bg-emerald-50 rounded-3xl border border-emerald-100 text-center">
                        <p className="text-[10px] font-black uppercase tracking-widest text-emerald-600 mb-2">Completed</p>
                        <p className="text-3xl font-black text-emerald-700">{s.completedCount}</p>
                      </div>
                      <div className="p-5 bg-indigo-50 rounded-3xl border border-indigo-100 text-center">
                        <p className="text-[10px] font-black uppercase tracking-widest text-indigo-600 mb-2">Efficiency</p>
                        <p className="text-3xl font-black text-indigo-700">{s.completionRate}%</p>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-indigo-600 rounded-full"></div>
                        Current Workload Breakdown
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-4">
                          <div className="flex justify-between items-center text-xs font-bold">
                            <span className="text-slate-500 uppercase tracking-widest">To Do Queue</span>
                            <span className="text-slate-900">{s.todoCount} Tasks</span>
                          </div>
                          <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-slate-400 transition-all duration-1000" 
                              style={{ width: `${s.tasksCount > 0 ? (s.todoCount / s.tasksCount) * 100 : 0}%` }}
                            ></div>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div className="flex justify-between items-center text-xs font-bold">
                            <span className="text-indigo-500 uppercase tracking-widest">Active Pursuit</span>
                            <span className="text-indigo-900">{s.inProgressCount} Tasks</span>
                          </div>
                          <div className="h-2 w-full bg-indigo-50 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-indigo-500 transition-all duration-1000" 
                              style={{ width: `${s.tasksCount > 0 ? (s.inProgressCount / s.tasksCount) * 100 : 0}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="pt-6 border-t border-slate-100 flex justify-center">
                      <button 
                        onClick={() => setViewingStatsUser(null)}
                        className="px-10 py-4 bg-slate-900 text-white font-black uppercase tracking-widest text-[11px] rounded-2xl hover:bg-slate-800 transition-all shadow-xl shadow-slate-900/10 active:scale-95"
                      >
                        Dismiss Analysis
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {(showModal || editingUser) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white w-full max-w-md rounded-[2.5rem] shadow-2xl p-10 animate-in zoom-in-95">
            <h2 className="text-2xl font-black text-slate-900 mb-8 tracking-tight">
              {editingUser ? 'Edit Permissions' : 'Invite Member'}
            </h2>
            
            {invitationSuccess ? (
              <div className="text-center py-10 space-y-4 animate-in fade-in zoom-in-95">
                <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-3xl flex items-center justify-center mx-auto mb-6">
                  <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                </div>
                <h3 className="text-xl font-bold text-slate-900">Invitation Dispatched!</h3>
                <p className="text-slate-500 text-sm font-medium leading-relaxed">
                  The activation email has been sent. <br/> 
                  <strong>Required:</strong> The user must click the link in their inbox to activate their account.
                </p>
                <button 
                  onClick={() => { setShowModal(false); setInvitationSuccess(false); }}
                  className="mt-6 w-full py-4 bg-slate-900 text-white font-black uppercase text-[11px] rounded-xl hover:bg-slate-800 transition-all"
                >
                  Return to Directory
                </button>
              </div>
            ) : (
              <form onSubmit={handleSaveUser} className="space-y-6">
                {!editingUser && (
                  <>
                    <div>
                      <label className="block text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Full Name</label>
                      <input 
                        type="text" 
                        required
                        value={newUser.name}
                        onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                        className="w-full px-5 py-4 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-500 outline-none transition-all font-semibold shadow-sm text-slate-900"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Email Address</label>
                      <input 
                        type="email" 
                        required
                        value={newUser.email}
                        onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                        className="w-full px-5 py-4 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-500 outline-none transition-all font-semibold shadow-sm text-slate-900"
                        placeholder="member@organization.com"
                      />
                    </div>
                  </>
                )}
                <div>
                  <label className="block text-[11px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Platform Role</label>
                  <select 
                    className="w-full px-5 py-4 rounded-2xl border border-slate-200 bg-slate-50 focus:bg-white focus:border-indigo-500 outline-none font-bold text-slate-900 cursor-pointer shadow-sm"
                    value={editingUser ? editingUser.role : newUser.role}
                    onChange={(e) => {
                      const role = e.target.value as Role;
                      if (editingUser) setEditingUser({...editingUser, role});
                      else setNewUser({...newUser, role});
                    }}
                  >
                    <option value={Role.ADMIN}>Administrator</option>
                    <option value={Role.MANAGER}>Project Manager</option>
                    <option value={Role.TEAM_MEMBER}>Contributor</option>
                  </select>
                </div>

                {errorMessage && (
                  <div className="p-4 bg-rose-50 text-rose-600 text-[11px] font-bold rounded-xl border border-rose-100">
                    Error: {errorMessage}
                  </div>
                )}

                <div className="flex gap-4 pt-4">
                  <button 
                    type="button"
                    onClick={() => { setShowModal(false); setEditingUser(null); setErrorMessage(null); }}
                    className="flex-1 py-4 bg-slate-100 text-slate-600 font-black uppercase tracking-widest text-[11px] rounded-2xl hover:bg-slate-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    disabled={isSyncing}
                    className="flex-1 py-4 bg-indigo-600 text-white font-black uppercase tracking-widest text-[11px] rounded-2xl hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-500/20 active:scale-95"
                  >
                    {isSyncing ? 'Processing...' : editingUser ? 'Update Role' : 'Send Invitation'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;